function renderPageContentClientModuleTemplate({
  join,
  hasCreateLink,
  hasFiltersUseQueryStates,
  hasDeleteParseAsArrayOf,
  hasFiltersEntityFiltersBar,
  devModeIntegrationButtonExportName,
  devModeIntegrationButtonImportPath
}: {
  join: string
  hasCreateLink: string
  hasFiltersUseQueryStates: string
  hasDeleteParseAsArrayOf: string
  hasFiltersEntityFiltersBar: string
  devModeIntegrationButtonExportName: string
  devModeIntegrationButtonImportPath: string
}): string {
  return `'use client'

import type { Setting } from '@admin/actions/settings'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ${join} } from 'lucide-react'
${hasCreateLink}import { parseAsInteger, parseAsString${hasFiltersUseQueryStates}${hasDeleteParseAsArrayOf}, useQueryState } from 'nuqs'
import * as React from 'react'
import { toast } from 'sonner'
${hasFiltersEntityFiltersBar}import { SortOrderDialog } from '@admin/components/shared/sort-order-dialog'
import { PageHeader } from '@admin/components/shared/page-header'
import { Button } from '@admin/components/ui/button'
import { ${devModeIntegrationButtonExportName} } from '${devModeIntegrationButtonImportPath}'
`
}

function renderPageContentImportsTemplate(): string {
  return `import { DeleteDialog } from '@admin/components/shared/delete-dialog'
`
}

function renderPageContentImportsSchemaNameTemplate({
  join,
  schemaName
}: {
  join: string
  schemaName: string
}): string {
  return `import {
  ${join}
} from '@admin/hooks/use-${schemaName}'
`
}

function renderPageContentImportsAdminTemplate({
  singular,
  actionImportPath,
  plural,
  hasDeleteAdmin,
  toKebabCase
}: {
  singular: string
  actionImportPath: string
  plural: string
  hasDeleteAdmin: string
  toKebabCase: string
}): string {
  return `import type { ${singular} } from '@admin/actions/${actionImportPath}'
import { get${plural}, bulkUpdate${plural}SortOrder } from '@admin/actions/${actionImportPath}'
${hasDeleteAdmin}import { ${plural}Table } from './${toKebabCase}-table'
`
}

function renderPageContentFilterParsersTemplate({
  resolvedFiltersDate,
  resolvedFiltersDateDate,
  resolvedFiltersDistinct
}: {
  resolvedFiltersDate: string
  resolvedFiltersDateDate: string
  resolvedFiltersDistinct: string
}): string {
  return `  const filterParsers = {
${resolvedFiltersDate}
  } as const
  const [filterValues, setFilters] = useQueryStates(filterParsers)
${resolvedFiltersDateDate}
${resolvedFiltersDistinct}
`
}

function renderPageContentSelectOptionsTemplate({
  filterVariableName
}: {
  filterVariableName: string
}): string {
  return `  const ${filterVariableName}SelectOptions = (${filterVariableName}Options ?? []).map((option) => ({
    label: option,
    value: option
  }))`
}

function renderPageContentHandleBulkDeleteTemplate({
  plural,
  escapeTemplateText,
  escapeTemplateTextEscapedTemplateText,
  pluralText,
  quoteLiteral,
  quoteLiteralQuotedValue
}: {
  plural: string
  escapeTemplateText: string
  escapeTemplateTextEscapedTemplateText: string
  pluralText: string
  quoteLiteral: string
  quoteLiteralQuotedValue: string
}): string {
  return `
  const [selectedIds, setSelectedIds] = useQueryState(
    'selected',
    parseAsArrayOf(parseAsString).withDefault([])
  )
  const [isPending, startTransition] = React.useTransition()

  const handleBulkDelete = () => {
    startTransition(async () => {
      try {
        const result = await deleteBulk${plural}(selectedIds)

        if (result.success) {
          toast.success(
            selectedIds.length === 1
              ? \`\${selectedIds.length} ${escapeTemplateText} deleted\`
              : \`\${selectedIds.length} ${escapeTemplateTextEscapedTemplateText} deleted\`
          )
          queryClient.refetchQueries({ queryKey: ['${pluralText}'] })
          void setSelectedIds([])
        } else {
          toast.error(result.error || ${quoteLiteral})
        }
      } catch (error) {
        toast.error(${quoteLiteralQuotedValue})
        console.error(error)
      }
    })
  }
`
}

function renderPageContentResultTemplate({
  plural,
  pluralText,
  camelPlural,
  labelExpr
}: {
  plural: string
  pluralText: string
  camelPlural: string
  labelExpr: string
}): string {
  return `
  const [sortDialogOpen, setSortDialogOpen] = React.useState(false)
  const { data: sortOrderSummary } = useQuery({
    queryKey: ['${plural}', 'sort-order-summary'],
    queryFn: () => get${pluralText}({ limit: 1, offset: 0 })
  })
  const sortOrderTotalRows = sortOrderSummary?.total ?? 0

  const fetchSortPage = React.useCallback(
    async (offset: number, limit: number) => {
      const result = await get${pluralText}({ limit, offset })
      return {
        items: (result.${camelPlural} ?? []).map((item) => ({
          id: item.id,
          label: ${labelExpr}
        })),
        total: result.total
      }
    },
    []
  )

  const handleSaveSortOrder = React.useCallback(
    async (updates: { id: string; sortOrder: number }[]) => {
      const result = await bulkUpdate${pluralText}SortOrder(updates)
      if (result.success) {
        queryClient.refetchQueries({ queryKey: ['${plural}'] })
      }
      return result
    },
    [queryClient]
  )
`
}

function renderPageContentTemplate({ singular }: { singular: string }): string {
  return `
            {selectedIds.length > 0 && (
              <DeleteDialog
                count={selectedIds.length}
                entityName="${singular}"
                onConfirm={handleBulkDelete}
                isPending={isPending}
              />
            )}`
}

function renderPageContentActionButtonsTemplate({
  adminRoutePath,
  schemaName,
  singularizeLabel
}: {
  adminRoutePath: string
  schemaName: string
  singularizeLabel: string
}): string {
  return `<Button render={<Link href="${adminRoutePath}/${schemaName}/new" />}>
              Create ${singularizeLabel}
            </Button>`
}

function renderPageContentActionButtonsDetailsTemplate(): string {
  return `
            <Button variant="outline" onClick={() => setSortDialogOpen(true)} disabled={sortOrderTotalRows === 0}>
              <GripVertical className="size-4" />
              Rearrange
            </Button>`
}

function renderPageContentLayoutTemplate({
  deleteButton,
  rearrangeButton
}: {
  deleteButton: string
  rearrangeButton: string
}): string {
  return `          <div className="flex items-center gap-1">${deleteButton}${rearrangeButton}
          </div>`
}

function renderPageContentLayoutDetailsTemplate({
  rearrangeButton
}: {
  rearrangeButton: string
}): string {
  return `          <div className="flex items-center gap-1">${rearrangeButton}
          </div>`
}

function renderPageContentLabelTemplate({
  plural,
  schemaLabel
}: {
  plural: string
  schemaLabel: string
}): string {
  return `
        <SortOrderDialog
          open={sortDialogOpen}
          onOpenChange={setSortDialogOpen}
          queryKey={['${plural}']}
          fetchPage={fetchSortPage}
          onSave={handleSaveSortOrder}
          title="Rearrange ${schemaLabel}"
          description="Drag items to reorder them."
        />`
}

function renderPageContentComponentTemplate({
  imports,
  plural,
  singular,
  pluralText,
  filterStateLogicContent,
  deleteLogic,
  sortOrderLogic,
  filterOptionsLogicContent,
  activeFilterCountExpr,
  hasFiltersContent,
  hasFiltersSetFilters,
  filterConfigEntriesContent,
  schemaLabel,
  devModeIntegrationButtonExportName,
  createButtonContent,
  hasFiltersLabel,
  tableProps,
  sortOrderDialog
}: {
  imports: string
  plural: string
  singular: string
  pluralText: string
  filterStateLogicContent: string
  deleteLogic: string
  sortOrderLogic: string
  filterOptionsLogicContent: string
  activeFilterCountExpr: string
  hasFiltersContent: string
  hasFiltersSetFilters: string
  filterConfigEntriesContent: string
  schemaLabel: string
  devModeIntegrationButtonExportName: string
  createButtonContent: string
  hasFiltersLabel: string
  tableProps: string
  sortOrderDialog: string
}): string {
  return `${imports}
interface ${plural}PageContentProps<TValue> {
  columns: ColumnDef<${singular}, TValue>[]
  initialSettings?: Setting | null
}

export function ${plural}PageContent<TValue>({
  columns,
  initialSettings
}: ${plural}PageContentProps<TValue>) {
  const queryClient = useQueryClient()
  const isSearchFetching = useIsFetching({ queryKey: ['${pluralText}'] }) > 0
  const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''))
  const [, setPageIndex] = useQueryState('page', parseAsInteger.withDefault(0))
${filterStateLogicContent}${deleteLogic}${sortOrderLogic}
${filterOptionsLogicContent}  const activeFilterCount = ${activeFilterCountExpr}

  const handleSearch = React.useCallback((value: string | null) => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(value)
    })
  }, [setSearch, setPageIndex])

  const handleClearAll = React.useCallback(() => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(null)
${hasFiltersContent}
    })
  }, [setSearch, setPageIndex${hasFiltersSetFilters}])
${filterConfigEntriesContent}
  return (
    <React.Fragment>
      <PageHeader
        title="${schemaLabel}"
        actions={
          <div className="flex items-center gap-1">
            <${devModeIntegrationButtonExportName} initialSettings={initialSettings} />
${createButtonContent}
          </div>
        }
      />
${hasFiltersLabel}
      <main className="space-y-4 px-4 pb-4 flex-1">
        <${plural}Table ${tableProps} />${sortOrderDialog}
      </main>
    </React.Fragment>
  )
}
`
}

function renderPageContentDetailsTemplate({
  clearFilterObject
}: {
  clearFilterObject: string
}): string {
  return `      void setFilters({
${clearFilterObject}
      })`
}

function renderPageContentFiltersTemplate({
  filterConfigEntries
}: {
  filterConfigEntries: string
}): string {
  return `
  const filters = [
${filterConfigEntries}
  ]
`
}

function renderPageContentLabelBranchTemplate({
  toLowerCase,
  filtersActionsContent
}: {
  toLowerCase: string
  filtersActionsContent: string
}): string {
  return `
      <EntityFiltersBar
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={(updates) => {
          React.startTransition(() => {
            void setPageIndex(0)
            void setFilters(updates)
          })
        }}
        onClearAll={handleClearAll}
        search={search}
        searchPlaceholder="Search ${toLowerCase}..."
        isPending={isSearchFetching}
        onSearch={handleSearch}
        actions={
${filtersActionsContent}
        }
      />`
}

export function renderPageContentFile({
  lucideIcons,
  hasCreate,
  hasFilters,
  hasDelete,
  devModeIntegrationButtonExportName,
  devModeIntegrationButtonImportPath,
  hookImportNames,
  schemaName,
  singular,
  Singular,
  actionImportPath,
  Plural,
  pluralKebab,
  resolvedFiltersDate,
  resolvedFiltersDateDate,
  resolvedFiltersDistinct,
  filterOptions,
  pluralText,
  escapedSingularNoun,
  escapedPluralNoun,
  deleteFailureMessage,
  deleteFailureRetryMessage,
  plural,
  camelPlural,
  labelExpr,
  adminRoutePath,
  singularizeLabel,
  clearFilterObject,
  activeFilterCountExpr,
  filterConfigEntries,
  schemaLabel,
  filterPropsStr
}: {
  lucideIcons: string
  hasCreate: boolean
  hasFilters: boolean
  hasDelete: boolean
  devModeIntegrationButtonExportName: string
  devModeIntegrationButtonImportPath: string
  hookImportNames: string[]
  schemaName: string
  singular: string
  Singular: string
  actionImportPath: string
  Plural: string
  pluralKebab: string
  resolvedFiltersDate: string
  resolvedFiltersDateDate: string
  resolvedFiltersDistinct: string
  filterOptions: Array<
    { kind: 'distinct'; filterVariableName: string } | { kind: 'static'; content: string }
  >
  pluralText: string
  escapedSingularNoun: string
  escapedPluralNoun: string
  deleteFailureMessage: string
  deleteFailureRetryMessage: string
  plural: string
  camelPlural: string
  labelExpr: string
  adminRoutePath: string
  singularizeLabel: string
  clearFilterObject: string
  activeFilterCountExpr: string
  filterConfigEntries: string
  schemaLabel: string
  filterPropsStr: string
}): string {
  let imports = renderPageContentClientModuleTemplate({
    join: lucideIcons,
    hasCreateLink: hasCreate ? "import Link from 'next/link'\n" : '',
    hasFiltersUseQueryStates: hasFilters ? ', useQueryStates' : '',
    hasDeleteParseAsArrayOf: hasDelete ? ', parseAsArrayOf' : '',
    hasFiltersEntityFiltersBar: hasFilters
      ? "import { EntityFiltersBar } from '@admin/components/shared/entity-filters-bar'\n"
      : '',
    devModeIntegrationButtonExportName,
    devModeIntegrationButtonImportPath
  })

  if (hasDelete) imports += renderPageContentImportsTemplate()

  if (hookImportNames.length > 0) {
    imports += renderPageContentImportsSchemaNameTemplate({
      join: hookImportNames.join(',\n  '),
      schemaName
    })
  }

  imports += renderPageContentImportsAdminTemplate({
    singular: Singular,
    actionImportPath,
    plural: Plural,
    hasDeleteAdmin: hasDelete
      ? `import { deleteBulk${Plural} } from '@admin/actions/${actionImportPath}'\n`
      : '',
    toKebabCase: pluralKebab
  })

  const filterStateLogic = hasFilters
    ? renderPageContentFilterParsersTemplate({
        resolvedFiltersDate,
        resolvedFiltersDateDate,
        resolvedFiltersDistinct
      })
    : ''

  const filterOptionsLogic = filterOptions
    .map((filterOption) =>
      filterOption.kind === 'distinct'
        ? renderPageContentSelectOptionsTemplate({
            filterVariableName: filterOption.filterVariableName
          })
        : filterOption.content
    )
    .filter(Boolean)
    .join('\n')

  const deleteLogic = hasDelete
    ? renderPageContentHandleBulkDeleteTemplate({
        plural: Plural,
        escapeTemplateText: escapedSingularNoun,
        escapeTemplateTextEscapedTemplateText: escapedPluralNoun,
        pluralText,
        quoteLiteral: deleteFailureMessage,
        quoteLiteralQuotedValue: deleteFailureRetryMessage
      })
    : ''

  const sortOrderLogic = renderPageContentResultTemplate({
    plural,
    pluralText: Plural,
    camelPlural,
    labelExpr
  })

  const deleteButton = hasDelete ? renderPageContentTemplate({ singular }) : ''
  const createButton = hasCreate
    ? renderPageContentActionButtonsTemplate({
        adminRoutePath,
        schemaName,
        singularizeLabel
      })
    : ''
  const rearrangeButton = renderPageContentActionButtonsDetailsTemplate()
  const filtersActionsContent = hasDelete
    ? renderPageContentLayoutTemplate({ deleteButton, rearrangeButton })
    : renderPageContentLayoutDetailsTemplate({ rearrangeButton })
  const tableProps = filterPropsStr
    ? `columns={columns} ${hasDelete ? 'selectedIds={selectedIds} setSelectedIds={setSelectedIds}' : 'selectedIds={[]} setSelectedIds={() => {}}'} search={search} ${filterPropsStr}`
    : `columns={columns} ${hasDelete ? 'selectedIds={selectedIds} setSelectedIds={setSelectedIds}' : 'selectedIds={[]} setSelectedIds={() => {}}'} search={search}`
  const sortOrderDialog = renderPageContentLabelTemplate({ plural, schemaLabel })

  return renderPageContentComponentTemplate({
    imports,
    plural: Plural,
    singular: Singular,
    pluralText: plural,
    filterStateLogicContent: filterStateLogic ? `\n${filterStateLogic}` : '',
    deleteLogic,
    sortOrderLogic,
    filterOptionsLogicContent: filterOptionsLogic ? `${filterOptionsLogic}\n` : '',
    activeFilterCountExpr,
    hasFiltersContent: hasFilters ? renderPageContentDetailsTemplate({ clearFilterObject }) : '',
    hasFiltersSetFilters: hasFilters ? ', setFilters' : '',
    filterConfigEntriesContent: filterConfigEntries
      ? renderPageContentFiltersTemplate({ filterConfigEntries })
      : '',
    schemaLabel,
    devModeIntegrationButtonExportName,
    createButtonContent: createButton ? `            ${createButton}` : '',
    hasFiltersLabel: renderPageContentLabelBranchTemplate({
      toLowerCase: schemaLabel.toLowerCase(),
      filtersActionsContent
    }),
    tableProps,
    sortOrderDialog
  })
}
